import { Grid, GridColumn, Tooltip, TooltipToggle } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { Invoice } from '../../../stores/models/Invoice';
import { InvoiceSettlement } from '../../../stores/models/InvoiceSettlement';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';
import { formatDate } from '../../../util/DateUtil';

interface IAmountWithSettlementsTooltipProps {
  translateStore?: ITranslateStore;
  invoice: Invoice;
  id: number;
}

@inject('translateStore')
@observer
export default class PaidAmountWithSettlementsTooltip extends React.Component<IAmountWithSettlementsTooltipProps> {
  public render(): JSX.Element {
    const invoice = this.props.invoice;

    if (invoice.amount >= 0) {
      return this.invoicePaidAmount();
    } else {
      return this.creditInvoiceSettlements();
    }
  }

  private invoicePaidAmount() {
    const invoice = this.props.invoice;

    if (invoice.settledByCorrectionTransaction) {
      return this.renderTooltip('invoices.previous-invoices.settlement-as-correction-transaction');
    } else {
      const tooltipTitleLabel = invoice.unsettledAmount > 0 ? 'invoices.previous-invoices.partly-paid' : 'invoices.previous-invoices.fully-paid';
      return this.renderTooltip(tooltipTitleLabel);
    }
  }

  private renderTooltip(tooltipTitleLabel: string) {
    const invoice = this.props.invoice;
    const translate = this.props.translateStore.translate;
    const id = this.props.id;

    return (
      <Tooltip fixedWidth={false} referenceElement={<TooltipToggle id={`invoice-total-tooltip-${id}`}>{formatAsCurrency(invoice.paidAmount)}</TooltipToggle>}>
        <p className="h5">{translate(tooltipTitleLabel)}</p>
        {invoice.settlements.map((settlement, settlementKey) => this.settlement(settlement, settlementKey))}
      </Tooltip>
    );
  }

  private creditInvoiceSettlements() {
    const invoice = this.props.invoice;
    const translate = this.props.translateStore.translate;
    const id = this.props.id;

    return (
      <Tooltip fixedWidth={false} referenceElement={<TooltipToggle id={`invoice-total-tooltip-${id}`}>&mdash;</TooltipToggle>}>
        <p className="h5">{translate('invoices.previous-invoices.credit-invoice')}</p>
        {invoice.settlements.map((settlement, settlementKey) => this.settlement(settlement, settlementKey))}
      </Tooltip>
    );
  }

  private settlement(settlement: InvoiceSettlement, settlementKey: number) {
    const translate = this.props.translateStore.translate;

    let dateOrArchived: string;

    if (settlement.type === InvoiceSettlement.TYPE_ARCHIVED) {
      dateOrArchived = translate('invoices.previous-invoices.settlement-is-archived');
    } else {
      dateOrArchived = formatDate(settlement.date);
    }

    const settlementDateAndAmount = (
      <Grid align={['between-xs']} className="text-small" key={settlementKey}>
        <GridColumn width={['xs']}>{dateOrArchived}</GridColumn>
        <GridColumn width={['xs']} className="text-bold">
          {formatAsCurrency(settlement.amount)}
        </GridColumn>
      </Grid>
    );

    let descriptionMessage = null;

    if (settlement.type === InvoiceSettlement.TYPE_CREDIT_INVOICE) {
      descriptionMessage = translate('invoices.previous-invoices.settlement-by-credit-invoice', {
        invoiceNumber: settlement.invoiceNumber
      } as any);
    }

    if (settlement.type === InvoiceSettlement.TYPE_INVOICE) {
      descriptionMessage = translate('invoices.previous-invoices.settlement-by-invoice', {
        invoiceNumber: settlement.invoiceNumber
      } as any);
    }

    if (settlement.type === InvoiceSettlement.TYPE_PREPAYMENT) {
      descriptionMessage = translate('invoices.previous-invoices.settlement-as-prepayment');
    }

    if (settlement.type === InvoiceSettlement.TYPE_CORRECTION_TRANSACTION) {
      descriptionMessage = translate(`invoices.previous-invoices.settlement-as-correction.${settlement.externalLabelKey}`);
    }

    if (descriptionMessage == null) {
      return <React.Fragment key={settlementKey}>{settlementDateAndAmount}</React.Fragment>;
    } else {
      return (
        <React.Fragment key={settlementKey}>
          {settlementDateAndAmount}
          <Grid align={['between-xs']} className="text-small">
            <GridColumn width={['xs']}>{descriptionMessage}</GridColumn>
          </Grid>
        </React.Fragment>
      );
    }
  }
}
